import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { MaskLines } from "../site/motion";
import { IMAGES } from "../site/data";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const overlay = useTransform(scrollYProgress, [0, 1], [0.35, 0.7]);

  return (
    <section id="hero" ref={ref} data-testid="hero-section" className="relative h-[100svh] w-full overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={IMAGES.hero} alt="Calm water at dusk" className="h-full w-full object-cover" />
      </motion.div>
      <motion.div style={{ opacity: overlay }} className="absolute inset-0 bg-primary" />
      <div className="absolute inset-0 bg-gradient-to-t from-primary/70 via-transparent to-primary/20" />

      <div className="relative z-10 h-full mx-auto max-w-[1600px] px-6 md:px-10 flex flex-col justify-end pb-20 md:pb-28">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15, duration: 1 }}
          className="text-primary-foreground/80 text-xs md:text-sm font-semibold uppercase tracking-[0.28em] mb-6"
        >
          Amar Sagoo Nursing Inc — Addiction Medicine
        </motion.p>

        <h1 className="font-serif font-light text-primary-foreground leading-[0.92] text-5xl sm:text-7xl lg:text-[8.5rem]">
          <MaskLines lines={["Recovery,", "delivered with", "quiet dignity."]} />
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 1 }}
          className="mt-10 flex flex-col md:flex-row md:items-center gap-6 md:gap-10 max-w-2xl"
        >
          <p className="text-primary-foreground/85 text-base md:text-lg leading-relaxed">
            Compassionate, evidence-based care for substance use disorders — guided by nurses who treat the person, never the label.
          </p>
        </motion.div>

        <motion.button
          data-testid="hero-scroll-cue"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 1 }}
          onClick={() => document.getElementById("about")?.scrollIntoView({ behavior: "smooth" })}
          className="mt-12 flex items-center gap-3 text-primary-foreground/70 hover:text-primary-foreground transition-colors w-fit"
        >
          <span className="text-xs uppercase tracking-[0.2em]">Scroll</span>
          <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.6 }}>
            <ArrowDown size={16} />
          </motion.span>
        </motion.button>
      </div>
    </section>
  );
};
